import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { Popover } from 'primeng/popover';
import { TranslateService } from '@ngx-translate/core';
import { BooleanResponseResult, KeyValueModel, NoteProxy, NoteTagsLookupModel, NoteTagViewModel, NoteViewModel } from '@/app/proxy/proxies';
import { DialogService } from 'primeng/dynamicdialog';
import { finalize } from 'rxjs';
import { SharedModule } from '@/app/shared/shared.module';
import { ToastService } from '@/app/core/services/toast.service';
import { PlantationService } from '@/app/core/services/plantation.service';
import { CropHealthNoteFormComponent } from '../crop-health-note-form/crop-health-note-form.component';
import { CropHealthNoteDeleteConfirmComponent } from '../crop-health-note-list/crop-health-note-delete-confirm/crop-health-note-delete-confirm.component';
import { CropHealthNoteGetDirectionComponent } from '../crop-health-note-list/crop-health-note-get-direction/crop-health-note-get-direction.component';
import { CropHealthService } from '../../../../services/crop-health.service';
import { PermissionService } from '@/app/core/services/permission.service';
import { MixpanelTrackerService } from '@/app/core/analytics';

@Component({
  selector: 'app-crop-health-note-item',
  imports: [SharedModule],
  templateUrl: './crop-health-note-item.component.html',
  styleUrl: './crop-health-note-item.component.less',
  providers: [DialogService, NoteProxy, TranslateService],
})
export class CropHealthNoteItemComponent implements OnInit {
  @Input() note: NoteViewModel;
  @Input() showFullContent = false;

  noteTags: NoteTagsLookupModel[] = [];
  noteTypes: KeyValueModel[] = [];

  @Output() getAllNotes = new EventEmitter();
  @Output() viewNote = new EventEmitter();

  @ViewChild('noteActionPopover') noteActionPopover!: Popover;

  showActions = false;
  deleteLoading = false;

  constructor(
    private dialogService: DialogService,
    private noteProxy: NoteProxy,
    private translateService: TranslateService,
    private toastService: ToastService,
    public plantationService: PlantationService,
    private cropHealthService: CropHealthService,
    public permissionService: PermissionService,
    private mixpanelTracker: MixpanelTrackerService
  ) {
    window.addEventListener('click', (e: MouseEvent) => {
      const htmlElement = e.target as HTMLElement;

      const isElementInFilter = document.getElementById('note-actions')?.contains(htmlElement);

      if (!isElementInFilter) {
        this.showActions = false;
      }
    });
  }

  get displayNoteContent(): string {
    const content = this.note?.noteContent ?? '';
    if (this.showFullContent || content.length <= 60) {
      return content;
    }

    return `${content.slice(0, 60)}...`;
  }

  ngOnInit(): void {
    this.cropHealthService.noteLookups$.subscribe(res => {
      this.noteTags = res.noteTags ?? [];
      this.noteTypes = res.noteTypes ?? [];
    })
  }

  onShowActions(event: any) {
    event.stopPropagation();
    // this.showActions = !this.showActions;

    if (this.noteActionPopover.el.nativeElement.classList.contains('p-popover-open')) {
      this.noteActionPopover.hide();
    } else {
      this.noteActionPopover.show(event);
    }
  }

  edit(event: MouseEvent) {
    this.noteActionPopover.hide();
    event.stopPropagation();

    if (!this.permissionService.isGrantedOnUserPermissions('Note_Update')) {
      return;
    }
    const modalRef = this.dialogService.open(CropHealthNoteFormComponent, {
      header: this.translateService.instant('doktar_app.services.crop_monitoring.edit_note'),
      modal: true,
      closable: true,
      width: '400px',
      inputValues: {
        note: this.note,
      },
    });

    modalRef.onClose.subscribe((res) => {
      if (res) {
        this.getAllNotes.emit();
      }
    });
  }

  share(event: MouseEvent) {
    if (!this.note?.coordinate) {
      return;
    }
    this.noteActionPopover.hide();
    event.stopPropagation();
    this.showActions = false;
    const { latitude, longitude } = this.note.coordinate;
    const url = `https://www.google.com/maps?q=${latitude},${longitude}`;
    const title = this.translateService.instant('doktar_app.land_plantations.common.location');
    const text = this.translateService.instant('doktar_app.crop_monitoring.share_note_coordinates_description');

    if (navigator.share) {
      navigator
        .share({
          title: title,
          text: text,
          url: url,
        })
        .then(() => {
          this.trackServicesNoteShared();
          console.log('Location shared successfully')
        })
        .catch((err) => console.error('Share failed:', err));
    } else {
      // Fallback: copy link or show alert
      console.warn('Web Share API is not supported in this browser.');
      alert(`Copy this location:\n${url}`);
      this.trackServicesNoteShared();
    }
  }

  getDirection(event: MouseEvent) {
    if (!this.note?.coordinate) {
      return;
    }
    this.noteActionPopover.hide();
    event.stopPropagation();
    this.showActions = false;
    this.dialogService.open(CropHealthNoteGetDirectionComponent, {
      header: this.translateService.instant('doktar_app.common.get_directions'),
      modal: true,
      closable: true,
      inputValues: {
        coordinates: {
          lat: this.note.coordinate.latitude,
          lng: this.note.coordinate.longitude,
        },
        // Mixpanel: direction-requested event needs note details (tags/content/photos).
        note: this.note,
      },
    });
  }

  deleteNote(event: MouseEvent) {
    this.noteActionPopover.hide();
    event.stopPropagation();
    this.showActions = false;

    if (!this.permissionService.isGrantedOnUserPermissions('Note_Delete')) {
      return;
    }

    const modalRef = this.dialogService.open(CropHealthNoteDeleteConfirmComponent, {
      header: this.translateService.instant('doktar_app.account.delete_account.title'),
      modal: true,
      closable: true,
      width: '343px',
      inputValues: {
        description: 'doktar_app.services.crop_monitoring.delete_note_message',
        confirmBtnLabel: 'doktar_app.services.crop_monitoring.delete_note_button',
        cancelBtnLabel: 'doktar_app.common.no_cancel',
      }
    });

    modalRef.onClose.subscribe((confirmRes) => {
      if (confirmRes) {
        this.deleteLoading = true;
        this.noteProxy
          .note_DeleteNote(this.note.id)
          .pipe(
            finalize(() => {
              this.deleteLoading = false;
            })
          )
          .subscribe({
            next: (res: BooleanResponseResult) => {
              if (res?.isSuccess) {
                this.trackServicesNoteDeleted();
                this.toastService.success({ detail: 'doktar_app.services.crop_monitoring.delete_note_success_message' });
                this.getAllNotes.emit();
              }
            },
          });
      }
    });
  }

  getTagIcon(tag: NoteTagViewModel) {
    if (tag) {
      const noteTag: NoteTagsLookupModel = this.noteTags.find((e) => e.key === tag.id);
      return noteTag?.icon;
    }
    return null;
  }

  private trackServicesNoteShared(): void {
    if (!this.note || !this.plantationService.selectedPlantation) {
      return;
    }

    const land = this.plantationService.selectedLand;
    const enterpriseId = land?.enterpriseId || this.plantationService.enterpriseId;
    const enterprise = this.plantationService.originalResult?.find((item) => item.id === enterpriseId);
    const problemType = this.note.noteTags?.map((item) => item.name).filter(Boolean).join(', ');

    this.mixpanelTracker.trackServicesNoteShared({
      'problem type': problemType || undefined,
      'description entered': !!this.note.noteContent?.trim(),
      'photo added': !!this.note.fileList?.length,
      'plantation id': this.plantationService.selectedPlantation.id,
      'plantation name': this.plantationService.selectedPlantation.name,
      'land id': land?.id,
      'land name': land?.name,
      'enterprise id': enterpriseId,
      'enterprise name': enterprise?.enterpriseName,
    });
  }

  private trackServicesNoteDeleted(): void {
    if (!this.note || !this.plantationService.selectedPlantation) {
      return;
    }

    const land = this.plantationService.selectedLand;
    const enterpriseId = land?.enterpriseId || this.plantationService.enterpriseId;
    const enterprise = this.plantationService.originalResult?.find((item) => item.id === enterpriseId);
    const problemType = this.note.noteTags?.map((item) => item.name).filter(Boolean).join(', ');

    this.mixpanelTracker.trackServicesNoteDeleted({
      'problem type': problemType || undefined,
      'description entered': !!this.note.noteContent?.trim(),
      'photo added': !!this.note.fileList?.length,
      'plantation id': this.plantationService.selectedPlantation.id,
      'plantation name': this.plantationService.selectedPlantation.name,
      'land id': land?.id,
      'land name': land?.name,
      'enterprise id': enterpriseId,
      'enterprise name': enterprise?.enterpriseName,
    });
  }
}
